/**
 * Completion for <Preference> and its children in the labeling setup's
 * code editor, in the shape of the platform's tags.json. Keep in step with
 * the tag's attributes (model.js) and docs/preference-tag.md.
 */
const attr = (name: string, description: string, type: string | string[] = "string", extra = {}) => ({
  name,
  description,
  type,
  required: false,
  ...extra,
});
const yesNo = ["true", "false"];
const kinds = ["text", "image", "audio", "video"];

export const preferenceSchema = {
  tags: {
    Preference: {
      name: "Preference",
      description:
        "Shows two or more options for the same context and records which one is preferred, with ties, 'neither', random order, hidden sources and per-criterion verdicts.",
      attrs: {
        name: attr("name", "Name of the element", "string", { required: true }),
        toName: attr("toName", "Name of the element the options are about. Leave out when the tag stands alone"),
        question: attr("question", "The question, shown above the options"),
        options: attr("options", "Task field holding the list of options ($field)"),
        mode: attr(
          "mode",
          "choose: pick the better one. scale: five steps between two options. rank: order all the options. best-worst: name the best and the worst of three or more",
          ["choose", "scale", "rank", "best-worst"],
          {
            default: "choose",
          },
        ),
        allowTie: attr("allowTie", "Offer 'About the same'", yesNo, { default: "false" }),
        allowNone: attr("allowNone", "Offer 'Neither is acceptable'", yesNo, { default: "false" }),
        randomize: attr("randomize", "Shuffle the options for each annotation; the order shown is saved", yesNo, {
          default: "false",
        }),
        blind: attr("blind", "Hide each option's label and source from the annotator", yesNo, { default: "false" }),
        layout: attr("layout", "How the options are laid out", ["auto", "inline", "list", "grid"], { default: "auto" }),
        kind: attr("kind", "What a plain string option is", kinds, { default: "text" }),
        required: attr("required", "A verdict is needed before submit", yesNo, { default: "false" }),
        requiredMessage: attr("requiredMessage", "The message shown when the verdict is missing"),
        tieLabel: attr("tieLabel", "Wording of the tie answer"),
        noneLabel: attr("noneLabel", "Wording of the 'neither' answer"),
      },
      children: ["Option", "Context", "Criterion"],
    },
    Option: {
      name: "Option",
      description: "One option of a <Preference>, read from a task field.",
      attrs: {
        value: attr("value", "The task field ($field)", "string", { required: true }),
        kind: attr("kind", "What the field holds", kinds),
        label: attr("label", "Shown beside the letter unless the tag is blind"),
        source: attr("source", "Who or what produced the option; kept for analysis"),
      },
    },
    Context: {
      name: "Context",
      description: "What the options of a <Preference> are about: an image, a text, a recording.",
      attrs: {
        value: attr("value", "The task field ($field)", "string", { required: true }),
        kind: attr("kind", "What the field holds", kinds, { default: "text" }),
        label: attr("label", "A small caption, such as Diagnosis"),
      },
    },
    Criterion: {
      name: "Criterion",
      description: "Something the options of a <Preference> are also compared on; each gets its own verdict.",
      attrs: {
        value: attr("value", "What is compared, such as 'Clear to a farmer'", "string", { required: true }),
        hint: attr("hint", "A short explanation shown with it"),
      },
    },
  },
  // Offered wherever a <View> takes children.
  inView: ["Preference"],
};
